import { useEffect, useState } from 'react';
import type { PaymentStatus, PaymentType } from '../lib/api/payments';

export interface PaymentFilters {
  types: PaymentType[];
  statuses: PaymentStatus[];
  urgency: boolean | null;
}

export const EMPTY_PAYMENT_FILTERS: PaymentFilters = {
  types: [],
  statuses: [],
  urgency: null,
};

export function countActivePaymentFilters(f: PaymentFilters): number {
  return f.types.length + f.statuses.length + (f.urgency !== null ? 1 : 0);
}

const TYPE_LABELS: Record<PaymentType, string> = {
  chi_thuong: 'Thanh toán',
  tam_ung: 'Tạm ứng',
  ung_luong: 'Ứng lương',
  hoan_ung: 'Hoàn ứng',
  thuong_nong: 'Thưởng nóng',
};

const STATUS_LABELS: Record<PaymentStatus, string> = {
  DRAFT: 'Nháp',
  PENDING_NVKT: 'Chờ NV.KT lập PC',
  PENDING_KTT: 'Chờ KTT duyệt',
  PENDING_CEO: 'Chờ CEO ký',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  DISBURSED: 'Đã chi',
  CANCELLED: 'Đã huỷ',
};

interface Props {
  open: boolean;
  initial: PaymentFilters;
  onClose: () => void;
  onApply: (f: PaymentFilters) => void;
}

export function PaymentFilterModal({ open, initial, onClose, onApply }: Props) {
  const [filters, setFilters] = useState<PaymentFilters>(initial);

  useEffect(() => {
    if (open) setFilters(initial);
  }, [open, initial]);

  if (!open) return null;

  const toggleType = (t: PaymentType) => {
    setFilters((f) => ({
      ...f,
      types: f.types.includes(t) ? f.types.filter((x) => x !== t) : [...f.types, t],
    }));
  };
  const toggleStatus = (s: PaymentStatus) => {
    setFilters((f) => ({
      ...f,
      statuses: f.statuses.includes(s) ? f.statuses.filter((x) => x !== s) : [...f.statuses, s],
    }));
  };
  const setUrgency = (v: boolean | null) => setFilters((f) => ({ ...f, urgency: v }));
  const reset = () => setFilters(EMPTY_PAYMENT_FILTERS);
  const apply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <div className="afm-backdrop" onClick={onClose}>
      <div className="afm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="afm-head">
          <h3>Bộ lọc phiếu chi</h3>
          <button className="afm-close" onClick={onClose} title="Đóng">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="afm-body">
          <div className="afm-group">
            <div className="afm-group-label">Loại đề nghị</div>
            {(['chi_thuong', 'tam_ung', 'hoan_ung', 'thuong_nong'] as PaymentType[]).map((t) => (
              <label key={t} className="afm-check">
                <input
                  type="checkbox"
                  checked={filters.types.includes(t)}
                  onChange={() => toggleType(t)}
                />
                <span>{TYPE_LABELS[t]}</span>
              </label>
            ))}
          </div>

          <div className="afm-group">
            <div className="afm-group-label">Trạng thái</div>
            {(Object.keys(STATUS_LABELS) as PaymentStatus[]).map((s) => (
              <label key={s} className="afm-check">
                <input
                  type="checkbox"
                  checked={filters.statuses.includes(s)}
                  onChange={() => toggleStatus(s)}
                />
                <span>{STATUS_LABELS[s]}</span>
              </label>
            ))}
          </div>

          <div className="afm-group">
            <div className="afm-group-label">Độ ưu tiên</div>
            <label className="afm-check">
              <input
                type="radio"
                name="pay-urgency"
                checked={filters.urgency === null}
                onChange={() => setUrgency(null)}
              />
              <span>Tất cả</span>
            </label>
            <label className="afm-check">
              <input
                type="radio"
                name="pay-urgency"
                checked={filters.urgency === true}
                onChange={() => setUrgency(true)}
              />
              <span>🔥 Khẩn / Đột xuất (duyệt trong ngày)</span>
            </label>
            <label className="afm-check">
              <input
                type="radio"
                name="pay-urgency"
                checked={filters.urgency === false}
                onChange={() => setUrgency(false)}
              />
              <span>📅 Bình thường (batch T6)</span>
            </label>
          </div>
        </div>

        <div className="afm-foot">
          <button className="afm-btn-ghost" onClick={reset}>Xoá bộ lọc</button>
          <div className="afm-foot-right">
            <button className="afm-btn-ghost" onClick={onClose}>Huỷ</button>
            <button className="afm-btn-primary" onClick={apply}>Áp dụng</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const PAYMENT_STATUS_LABELS = STATUS_LABELS;
export const PAYMENT_TYPE_LABELS = TYPE_LABELS;
